import { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from "moment";
import { tableConfig } from "../../../../commons/table/config/tableConfig";
import {
  getTrasladoEstadoColor,
  isTerminalEstadoTramo,
} from "../../../../../constants/trasladosEstados";
import {
  StyledTraslados,
  StyledTrasladosDialog,
  StyledTrasladosTable,
} from "../../../../styled/traslados";
import BuscarTrasladosFilter from "./buscarTrasladosFilter";
import CargarNegativoDirectoDialog from "./CargarNegativoDirectoDialog";
import CancelarTramoDialog from "./CancelarTramoDialog";
import AuditarTrasladoDialog from "../pendientes/AuditarTrasladoDialog";
import { mapTramoToAuditData } from "../trasladosAuditUtils";
import VisualizarTramoDialog from "./VisualizarTramoDialog";
import CancelarTrasladoCompletoDialog from "./CancelarTrasladoCompletoDialog";
import { LocalShipping } from "@mui/icons-material";

const BuscarTrasladosTable = ({
  filters,
  onSearch,
  onClear,
  onViewTraslado,
  onEditTraslado,
  onDuplicateTraslado,
  results = [],
  isFetching = false,
  soloGS = false,
  canManageTraslados = false,
}) => {

  const [negativoData, setNegativoData] = useState(null);
  const [cancelarData, setCancelarData] = useState(null);
  const [asignarData, setAsignarData] = useState(null);
  const [visualizarTramoData, setVisualizarTramoData] = useState(null);
  const [cancelarTrasladoData, setCancelarTrasladoData] = useState(null);

  const groupedData = useMemo(() => {
    const map = new Map();
    results.forEach(tramo => {
      if (!map.has(tramo.nroTraslado)) {
        map.set(tramo.nroTraslado, {
          ...tramo,
          tramos: [],
          distanciaTotal: 0
        });
      }
      const parent = map.get(tramo.nroTraslado);
      parent.tramos.push(tramo);
      parent.distanciaTotal += (tramo.distancia || 0);
      if (
        (!parent.nroFormulario || Number(parent.nroFormulario) <= 0) &&
        tramo.nroFormulario &&
        Number(tramo.nroFormulario) > 0
      ) {
        parent.nroFormulario = tramo.nroFormulario;
      }
      if (!parent.idSiniestro && tramo.idSiniestro) {
        parent.idSiniestro = tramo.idSiniestro;
      }
      if (!parent.idArt && tramo.idArt) {
        parent.idArt = tramo.idArt;
        parent.artName = tramo.artName;
      }
      if (parent.prestadorId == null && tramo.prestadorId != null) {
        parent.prestadorId = tramo.prestadorId;
      }
      const tieneAlta = tramo.siniestroTieneAltaMedica ?? tramo.SiniestroTieneAltaMedica;
      if (tieneAlta != null) {
        parent.siniestroTieneAltaMedica = Boolean(parent.siniestroTieneAltaMedica || tieneAlta);
      }
      const esRechazado = tramo.siniestroEsRechazado ?? tramo.SiniestroEsRechazado;
      if (esRechazado != null) {
        parent.siniestroEsRechazado = Boolean(parent.siniestroEsRechazado || esRechazado);
      }
      const siniestroEstado = tramo.siniestroEstado ?? tramo.SiniestroEstado;
      if (!parent.siniestroEstado && siniestroEstado) {
        parent.siniestroEstado = siniestroEstado;
      }
    });
    return Array.from(map.values());
  }, [results]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "nroTraslado",
        header: "Traslado",
        size: 80,
        minSize: 80,
        grow: false,
        Cell: ({ cell }) => <b>{cell.getValue()}</b>,
      },
      {
        accessorKey: "trabajador",
        header: "Trabajador",
      },
      {
        accessorKey: "siniestro",
        header: "Siniestro",
      },
      {
        id: "cantidadTramos",
        header: "Tramos",
        size: 70,
        minSize: 70,
        grow: false,
        Cell: ({ row }) => row.original.tramos.length
      },
      {
        accessorKey: "provinciaOrigen",
        header: "Origen",
        Cell: ({ row }) => `${row.original.provinciaOrigen || ""} - ${row.original.localidadOrigen || ""}`,
      },
      {
        accessorKey: "provinciaDestino",
        header: "Destino",
        Cell: ({ row }) => `${row.original.provinciaDestino || ""} - ${row.original.localidadDestino || ""}`,
      },
      {
        accessorKey: "fechaHoraTraslado",
        header: "Fecha",
        size: 90,
        minSize: 90,
        grow: false,
        Cell: ({ cell }) => cell.getValue() ? moment(cell.getValue()).format("DD/MM/YYYY") : "-",
      },
      {
        accessorKey: "gestor",
        header: "Gestor",
      },
      {
        accessorKey: "prestadorNombre",
        header: "Prestador",
        Cell: ({ row }) => (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <span>{row.original.prestadorNombre || "—"}</span>
            {row.original.tramos?.some(t => t.posibleNegativo) && (
              <StyledTrasladosTable.PosibleNegativoChip label="Posible Negativo" size="small" />
            )}
          </Box>
        ),
      },
      {
        id: "distanciaTotal",
        header: "Dist. Total",
        size: 90,
        minSize: 90,
        grow: false,
        Cell: ({ row }) => `${row.original.distanciaTotal} km`,
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: groupedData,
    ...tableConfig,
    state: { isLoading: isFetching },
    enableRowActions: true,
    renderRowActions: ({ row }) => {
      const tramos = row.original.tramos || [];
      const hasActiveTramos = tramos.some(
        (t) => !isTerminalEstadoTramo(t.estadoTramoNombre)
      );
      const isActionable = hasActiveTramos;
      return (
        <StyledTrasladosTable.ActionGroup>
          <Tooltip title="Visualizar Traslado Completo">
            <IconButton
              size="small"
              onClick={() => onViewTraslado?.(row.original)}>
              <LocalShipping fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageTraslados && (
            <>
              {isActionable && (
                <Tooltip title="Editar traslado (Abre FPT)">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onEditTraslado?.(row.original)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {(() => {
                const isBlockedAlta = Boolean(row.original.siniestroTieneAltaMedica);
                const isBlockedRechazado = Boolean(row.original.siniestroEsRechazado);
                const isBlocked = isBlockedAlta || isBlockedRechazado;
                const duplicateTooltip = isBlockedRechazado
                  ? "El siniestro se encuentra rechazado. No puede solicitar traslado."
                  : isBlockedAlta
                    ? "El siniestro tiene alta médica. No puede solicitar traslado."
                    : "Duplicar traslado";

                return (
                  <Tooltip title={duplicateTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={isBlocked}
                        onClick={() => onDuplicateTraslado?.(row.original)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })()}
              {hasActiveTramos && (
                <Tooltip title="Cancelar traslado completo">
                  <span>
                    <StyledTrasladosTable.DarkRedIconButton
                      size="small"
                      onClick={() => setCancelarTrasladoData(row.original)}>
                      <CancelIcon fontSize="small" />
                    </StyledTrasladosTable.DarkRedIconButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </StyledTrasladosTable.ActionGroup>
      );
    },
    positionActionsColumn: 'last',
    renderDetailPanel: ({ row }) => (
      <StyledTrasladosTable.DetailPanel>
        <Table size="small">
          <StyledTrasladosTable.DetailTableHead>
            <TableRow>
              <TableCell>Nro Tramo</TableCell>
              <TableCell>Origen</TableCell>
              <TableCell>Destino</TableCell>
              <TableCell>Transporte</TableCell>
              <TableCell>Agencia</TableCell>
              <TableCell>Estado Tramo</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </StyledTrasladosTable.DetailTableHead>
          <TableBody>
            {row.original.tramos.map((tramo) => {
              const isTerminalTramo = isTerminalEstadoTramo(tramo.estadoTramoNombre);
              const canReasignarAgencia = [
                "Pendiente de validación datos",
                "Pendiente Autorización",
                "Rechazado por Agencia",
              ].includes(tramo.estadoTramoNombre);
              return (
                <TableRow key={tramo.nroTramoUnico}>
                  <TableCell><b>{tramo.nroTramoUnico}</b></TableCell>
                  <TableCell>{`${tramo.provinciaOrigen || ""} - ${tramo.localidadOrigen || ""}`}</TableCell>
                  <TableCell>{`${tramo.provinciaDestino || ""} - ${tramo.localidadDestino || ""}`}</TableCell>
                  <TableCell>{tramo.tipoTransporteNombre}</TableCell>
                  <TableCell>{tramo.agenciaNombre || "—"}</TableCell>
                  <TableCell>
                    <StyledTrasladosDialog.StatusChip
                      label={tramo.estadoTramoNombre || "Sin estado"}
                      size="small"
                      chipColor={
                        tramo.estadoTramoColor ||
                        getTrasladoEstadoColor(tramo.estadoTramoNombre) ||
                        "#9e9e9e"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <StyledTrasladosTable.ActionGroup>
                      <Tooltip title="Visualizar tramo">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisualizarTramoData({ tramo, isAssigning: false });
                          }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!soloGS && !isTerminalTramo && (
                        <>
                          <Tooltip title="Cancelar tramo">
                            <span>
                              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCancelarData(tramo); }}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {!(tramo.posibleNegativo || tramo.estadoTramoNombre !== "Despachado") && (
                            <Tooltip title="Cargar Negativo">
                              <span>
                                <IconButton size="small" color="warning" onClick={() => setNegativoData(tramo)}>
                                  <ThumbDownOffAltIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {canReasignarAgencia && (
                            <Tooltip title={tramo.estadoTramoNombre === "Rechazado por Agencia" ? "Reasignar Agencia" : "Asignar Agencia"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAsignarData(mapTramoToAuditData(tramo, row.original));
                                  }}>
                                  <AssignmentIndIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </StyledTrasladosTable.ActionGroup>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTrasladosTable.DetailPanel>
    )
  });

  return (
    <Box p={2}>
      <StyledTraslados.Title variant="h6" gutterBottom>
        Buscar Traslados
      </StyledTraslados.Title>

      <BuscarTrasladosFilter initialValues={filters} onSearch={onSearch} onClear={onClear} />

      <MaterialReactTable table={table} />

      {visualizarTramoData && (
        <VisualizarTramoDialog
          open={Boolean(visualizarTramoData)}
          onClose={() => setVisualizarTramoData(null)}
          data={visualizarTramoData.tramo}
          isAssigning={visualizarTramoData.isAssigning}
          onRefresh={onSearch}
        />
      )}

      {negativoData && (
        <CargarNegativoDirectoDialog
          open={Boolean(negativoData)}
          onClose={() => setNegativoData(null)}
          data={negativoData}
          onRefresh={onSearch}
        />
      )}

      {cancelarData && (
        <CancelarTramoDialog
          open={Boolean(cancelarData)}
          onClose={() => setCancelarData(null)}
          data={cancelarData}
          onRefresh={onSearch}
        />
      )}

      {asignarData && (
        <AuditarTrasladoDialog
          open={Boolean(asignarData)}
          onClose={() => setAsignarData(null)}
          onComplete={() => {
            setAsignarData(null);
            onSearch(filters);
          }}
          data={asignarData}
          rol="at"
        />
      )}

      {cancelarTrasladoData && (
        <CancelarTrasladoCompletoDialog
          open={Boolean(cancelarTrasladoData)}
          onClose={() => setCancelarTrasladoData(null)}
          data={cancelarTrasladoData}
          onRefresh={onSearch}
        />
      )}
    </Box>
  );
};

export default BuscarTrasladosTable;
